import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Tabs, Tab, Alert } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import PharmacyHaloButton from "@/components/inputs/Button";
import { phappsRequest } from "@/services/apiService";
import {
  PHARMACY_HALO_COLORS,
  DOCLINK_COLORS,
  QUDA_COLORS,
} from "@/config/colors";
import { API_PATHS } from "@/config/apiConstants";
import QudaAnalytics from "@/components/analytics/quda/QudaAnalytics";
import DocLinkAnalytics from "@/components/analytics/doclink/DocLinkAnalytics";
import AdminChemistOverviewPanel from "@/components/admin/AdminChemistOverviewPanel";
import { getChemistName } from "@/utils/textUtils";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
const ChemistAnalytics = () => {
  const { chemistID, chemistUUID } = useParams();
  const resolvedChemistKey = chemistUUID ?? chemistID;
  const isAdminChemistRoute = Boolean(chemistUUID);
  const navigate = useNavigate();
  const [chemistData, setChemistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 28
    );
    return [startDate, today];
  });

  useEffect(() => {
    const fetchChemistData = async () => {
      try {
        setLoading(true);

        const [startDate, endDate] = dateRange;

        const params = isAdminChemistRoute
          ? {
              ChemistUUID: resolvedChemistKey,
              DateStart: startDate.toISOString().split("T")[0],
              DateEnd: endDate.toISOString().split("T")[0],
            }
          : {
              PHUUID: resolvedChemistKey,
              DateStart: startDate.toISOString().split("T")[0],
              DateEnd: endDate.toISOString().split("T")[0],
            };

        const response = await phappsRequest(
          isAdminChemistRoute
            ? API_PATHS.ADMIN_FUNCTIONS_CHEMIST_DETAIL
            : API_PATHS.CHEMIST_DATA,
          "GET",
          null,
          params
        );
        if (response && response.ChemistData) {
          setChemistData(response);
        } else {
          setError("Failed to fetch chemist data");
        }
      } catch (err) {
        console.error("Error fetching chemist data:", err);
        const msg = err?.message || "";
        const isNotFound =
          isAdminChemistRoute &&
          typeof msg === "string" &&
          msg.toLowerCase().includes("chemist not found");
        setError(
          isNotFound
            ? "Chemist not found."
            : getAdminApiErrorMessage(err, "Error fetching chemist data")
        );
      } finally {
        setLoading(false);
      }
    };

    if (resolvedChemistKey) {
      fetchChemistData();
    }
  }, [resolvedChemistKey, dateRange, isAdminChemistRoute]);

  useEffect(() => {
    if (!resolvedChemistKey || !isAdminChemistRoute) return;
    setSelectedTab(0);
  }, [resolvedChemistKey, isAdminChemistRoute]);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getActiveColor = () => {
    if (isAdminChemistRoute) {
      if (selectedTab === 0) return PHARMACY_HALO_COLORS.BLUE;
      if (selectedTab === 1) return QUDA_COLORS.TEAL_MID;
      return DOCLINK_COLORS.MID_PURPLE;
    }
    if (selectedTab === 0) return QUDA_COLORS.TEAL_MID;
    if (selectedTab === 1) return DOCLINK_COLORS.MID_PURPLE;
    return PHARMACY_HALO_COLORS.BLUE;
  };

  const getBackgroundColor = () => {
    if (isAdminChemistRoute) {
      if (selectedTab === 0) return "rgba(0, 33, 105, 0.06)";
      if (selectedTab === 1) return `${QUDA_COLORS.TEAL_LIGHT}40`;
      return `${DOCLINK_COLORS.LIGHT_PURPLE}40`;
    }
    if (selectedTab === 0) return `${QUDA_COLORS.TEAL_LIGHT}40`;
    if (selectedTab === 1) return `${DOCLINK_COLORS.LIGHT_PURPLE}40`;
    return "transparent";
  };

  if (loading) {
    return (
      <Box className="flex items-center justify-center h-64">
        <Typography variant="h6" color="textSecondary">
          Loading chemist data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex flex-col items-center justify-center h-64 space-y-4 px-4">
        <Alert severity="warning" sx={{ maxWidth: 560 }}>
          {error}
        </Alert>
        <PharmacyHaloButton
          onClick={() =>
            navigate(isAdminChemistRoute ? "/admin-functions/overview" : "/overview")
          }
        >
          <ArrowBack sx={{ mr: 1 }} />
          Back to Overview
        </PharmacyHaloButton>
      </Box>
    );
  }

  if (!chemistData || !chemistData.ChemistData) {
    return (
      <Box className="flex flex-col items-center justify-center h-64 space-y-4">
        <Typography variant="h6" color="textSecondary">
          No chemist data available
        </Typography>
        <PharmacyHaloButton
          onClick={() =>
            navigate(isAdminChemistRoute ? "/admin-functions/overview" : "/overview")
          }
        >
          <ArrowBack sx={{ mr: 1 }} />
          Back to Overview
        </PharmacyHaloButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: getBackgroundColor(),
        transition: "background-color 0.3s ease",
        p: 3,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        // Extend background to edges by counteracting parent padding
        mx: -2,
        my: -2,
        px: 4,
        py: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1fr auto 1fr" },
          gap: 2,
          alignItems: "center",
        }}
      >
        {/* Left: Back Button */}
        <Box sx={{ justifySelf: { xs: "center", xl: "start" } }}>
          <PharmacyHaloButton
            onClick={() =>
              navigate(isAdminChemistRoute ? "/admin-functions/overview" : "/overview")
            }
          >
            <ArrowBack sx={{ mr: 1 }} />
            Back to Overview
          </PharmacyHaloButton>
        </Box>

        {/* Center: Pharmacy Name */}
        <Box sx={{ justifySelf: "center", textAlign: "center" }}>
          <Typography
            variant="h4"
            className="font-bold"
            style={{ color: PHARMACY_HALO_COLORS.BLUE }}
          >
            {getChemistName(chemistData.ChemistData.chemistName)}
          </Typography>
        </Box>

        {/* Right: Date Range Selector */}
        <Box
          sx={{ justifySelf: { xs: "center", xl: "end" }, minWidth: "300px" }}
        >
          <SFDateRangeSelector
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </Box>
      </Box>

      {/* MUI Tabs */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            TabIndicatorProps={{
              style: {
                backgroundColor: getActiveColor(),
                height: "3px",
              },
            }}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: "normal",
                fontSize: "1rem",
                color: "#666",
              },
              "& .Mui-selected": {
                color: `${getActiveColor()} !important`,
                fontWeight: "bold",
              },
            }}
          >
            {isAdminChemistRoute && <Tab label="Overview" />}
            <Tab label="Quda Analytics" />
            <Tab label="DocLink Analytics" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: 2 }}>
          {isAdminChemistRoute && selectedTab === 0 && (
            <AdminChemistOverviewPanel chemistData={chemistData.ChemistData} />
          )}
          {((isAdminChemistRoute && selectedTab === 1) ||
            (!isAdminChemistRoute && selectedTab === 0)) && (
            <QudaAnalytics
              chemistData={chemistData.ChemistData}
              chemistID={resolvedChemistKey}
            />
          )}
          {((isAdminChemistRoute && selectedTab === 2) ||
            (!isAdminChemistRoute && selectedTab === 1)) && (
            <DocLinkAnalytics chemistData={chemistData.ChemistData} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChemistAnalytics;
